// src/components/PursuitAccess/ExportAssessmentsButton.jsx
import React, { useState } from "react";
import { Box, Button, Layer, Text, Heading } from "grommet";

// If you haven't already: npm i xlsx
// We'll lazy-load it. If import fails, we fallback to CSV.

const userTypeMap = {
    1: "Manager",
    2: "Invited User",
    0: "Self Registered User",
};

const rangeLabel = (value) => {
    switch (value) {
        case "1w": return "1 week";
        case "1m": return "1 month";
        case "3m": return "3 months";
        case "all": return "6 months";
        default: return "selected period";
    }
};

const toRows = (data) =>
    data.map((a) => {
        const mgrs = Array.isArray(a.managers) ? a.managers : [];

        const managerNames = mgrs.map(m => m?.manager_name).filter(Boolean).join("; ");
        const managerEmails = mgrs.map(m => m?.manager_email).filter(Boolean).join("; ");
        const managerCountries = mgrs.map(m => m?.manager_country).filter(Boolean).join("; ");

        return {
            Date: a.end_date ? new Date(a.end_date).toLocaleDateString() : "",
            "User Name": a.user_name && a.user_name !== "unknown" ? a.user_name : a.user_email,
            "User Email": a.user_email,
            Country: a.user_country,
            "User Type": userTypeMap[a.user_type] ?? a.user_type,
            "Agreed to Communicate": a.user_agree_to_communicate === 1 ? "Yes" : "No",
            Contacted: a.user_is_communicated === 1 ? "Yes" : "No",
            "Contacted At":
                a.user_communicated_at_date && a.user_communicated_at_time
                    ? `${a.user_communicated_at_date} ${a.user_communicated_at_time}`
                    : "",
            Comment: a.comment || "",
            // New: manager details (blank if none)
            "Manager Names": managerNames || "",
            "Manager Emails": managerEmails || "",
            "Manager Countries": managerCountries || "",
        };
    });


function downloadBlob(filename, mime, content) {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

function rowsToCSV(rows) {
    const headers = Object.keys(rows[0] || {});
    const escape = (v) => {
        const s = (v ?? "").toString();
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [
        headers.map(escape).join(","),
        ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
    ];
    return lines.join("\n");
}

const ExportAssessmentsButton = ({ data = [], dateRange = "all" }) => {
    const [open, setOpen] = useState(false);
    const disabled = !Array.isArray(data) || data.length === 0;
    const range = dateRange === 'all' ? '6m' : dateRange;

    const handleConfirm = async () => {
        setOpen(false);

        const rows = toRows(data);
        const fileSuffix = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
        const fname = `HPE Digital Skill Advisor - User self assessments - ${range} - ${fileSuffix}.xlsx`;

        try {
            const XLSX = await import("xlsx"); // lazy-load
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(rows);
            XLSX.utils.book_append_sheet(wb, ws, "Assessments");
            XLSX.writeFile(wb, fname); // triggers download
        } catch (e) {
            // Fallback to CSV
            const csv = rows.length ? rowsToCSV(rows) : "No data";
            downloadBlob(fname.replace(".xlsx", ".csv"), "text/csv;charset=utf-8", csv);
        }
    };

    return (
        <>
            <Button
                label="Export"
                onClick={() => setOpen(true)}
                secondary
                disabled={disabled}
            />
            {open && (
                <Layer
                    onEsc={() => setOpen(false)}
                    onClickOutside={() => setOpen(false)}
                    modal
                    responsive={false}
                    position="center"
                >
                    <Box pad="medium" width="medium" gap="small">
                        <Heading level={3} margin="none">Confirm Export</Heading>
                        <Text>
                            All the data for the past <strong>{rangeLabel(dateRange)}</strong> will be downloaded.
                            Are you sure you want to proceed?
                        </Text>
                        <Box direction="row" gap="small" justify="end" margin={{ top: "small" }}>
                            <Button label="No" onClick={() => setOpen(false)} />
                            <Button label="Yes" primary onClick={handleConfirm} />
                        </Box>
                    </Box>
                </Layer>
            )}
        </>
    );
};

export default ExportAssessmentsButton;
